import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { patchPost } from '../api'
import { SnackbarStackContext } from '../app/contexts'
import { getHookPromiseErrorHandler } from '../common'
import { useCrudInteractions, useLoadMore } from '../commonHooks'
import { GET_DEBOUNCE_DELAY } from '../constants'
import { debouncePromise } from '../debounce'
import model from '../model'

import {
  trackPriorityDocumentAdded,
  trackPriorityDocumentAddedByUndo,
  trackPriorityDocumentRemoved
} from '../tracking/segment'

import { getTypeFilters } from '../typeUtilities'

import { DEFAULT_ORDER_DIRECTION, DEFAULT_ORDER_BY } from './constants'
import { loadDocumentsPage } from './utilities'

const DEFAULT_LIMIT = 20
const ALL_CHILDREN = '__all-children__'

const loadDocumentsDebounced = debouncePromise(loadDocumentsPage, GET_DEBOUNCE_DELAY)
const newPost = () => ({})

/**
 * usePostLifeCycle creates a one-stop shop for managing collections of posts. It provides a posts array,
 * paging functions, search filters, and event handlers that are coordinated with each other in order to
 * present a consistent state.
 *
 * Note that `onSelect` is called with the selected post’s ID, or `undefined` when the selection is
 * cleared—something that happens whenever the child filter, search query, or sort order changes.
 * Consumers that can’t represent “nothing selected” need to handle that value explicitly.
 *
 * TODO This code was extracted from PostDocumentCollection and is currently duplicated to preserve
 *      PostDocumentCollection functionality. This can be consolidated sometime.
 */
const usePostLifeCycle = ({
  householdId,
  requestedClientId,
  autoselectPostId,
  requestedPostId,
  enforceTags,
  onApiError,
  onSelect,
  initialOrderBy,
  initialOrderDirection,
  multiselect,
  includeMedia
}) => {
  const limit = DEFAULT_LIMIT

  const [categoryFilter, setCategoryFilter] = useState([])
  const [orderBy, setOrderBy] = useState(initialOrderBy || DEFAULT_ORDER_BY)
  const [orderDirection, setOrderDirection] = useState(initialOrderDirection || DEFAULT_ORDER_DIRECTION)
  const [clientId, setClientId] = useState(requestedClientId || ALL_CHILDREN)
  const [filters, setFilters] = useState()
  const [query, setQuery] = useState('')
  const [priority, setPriority] = useState(null)
  const [inProgress, setInProgress] = useState()
  const [counts, setCounts] = useState({ totalQueryCount: 0, totalHouseholdCount: 0, unfinishedCount: 0 })
  const [categories, setCategories] = useState([])

  const postsLoader = useCallback(
    async cursor => {
      // At least one of household or child ID is required.
      if (!householdId && (!clientId || clientId === ALL_CHILDREN)) {
        return { itemsPage: null, cursorNext: null }
      }

      const params = new URLSearchParams()

      // Having a child ID takes precedence over a household ID.
      if (clientId && clientId !== ALL_CHILDREN) {
        params.set('childId', clientId)
      } else if (householdId) {
        params.set('householdId', householdId)
      }

      if (categoryFilter) {
        params.set('categories', categoryFilter)
      }

      if (enforceTags) {
        // Enforced “tags” are assumed to be arrays of arrays of tag IDs, which are integers.
        params.set('tags', enforceTags.flat())
      }

      if (query) {
        params.set('query', query)
      }

      if (orderBy) {
        params.set('orderBy', orderBy)
      }

      if (orderDirection) {
        params.set('sortDirection', orderDirection)
      }

      // inProgress is boolean so we need a direct null/undefined check.
      if (inProgress !== null && inProgress !== undefined) {
        params.set('inProgress', inProgress)
      }

      if (priority !== null && priority !== undefined) {
        params.set('priority', priority)
      }

      if (includeMedia) {
        params.set('includeMedia', true)
      }

      const documentsPage = await loadDocumentsDebounced(params, cursor, limit)
      const {
        totalQueryCount,
        totalHouseholdCount,
        unfinishedCount,
        categories: categoriesFromDocumentsPage
      } = documentsPage

      setCounts({ totalQueryCount, totalHouseholdCount, unfinishedCount })
      setCategories(categoriesFromDocumentsPage)
      return documentsPage
    },
    [
      householdId,
      clientId,
      categoryFilter,
      enforceTags,
      query,
      orderBy,
      orderDirection,
      inProgress,
      includeMedia,
      limit,
      priority
    ]
  )

  const { postsComplete, postsPaging, nextPostsPage, ...crudContext } = useLoadMore(postsLoader, onApiError, 'post')

  const { posts, postCreated, postUpdated, postDeleted, selectedPost, handleCreate, handleRequestSelect } =
    useCrudInteractions('post', { newPost }, crudContext)

  // We can switch to post creation if we don’t have a current post or if the current post has an ID.
  const selectedPostId = selectedPost?.id

  // The binder is truly empty if there are zero posts and there is no current query, category filter, nor priority filter.
  const binderIsEmpty = posts?.length === 0 && !query && categoryFilter?.length === 0 && !priority

  const clearSearch = () => {
    setQuery('')
  }

  // Leaving the priority filter goes through the same path as toggling it off in the toolbar.
  const clearPriority = () => handleSearchChange(setPriority)(null)

  // Post selection gets a wrapper to account for enableRedirects, which stipulates that the URL be changed
  // based on the selected post ID.
  const handlePostSelect = useCallback(
    post => {
      handleRequestSelect(post)

      if (onSelect) {
        onSelect(post?.id)
      }
    },
    [handleRequestSelect, onSelect]
  )

  // TODO Refactor boundary: convert a new post to its model. Eventually move to the calling creator code.
  const handlePostAdd = post => {
    const { media, ...addedPost } = model.post(post)
    postCreated(addedPost)
    handlePostSelect(addedPost)
  }

  // Removing a post from the list doesn’t clear the selection but moves it to an adjacent post, if asked to.
  // In multiselect mode, only the selected (displayed) post moves: `onSelect` toggles a post’s membership in the
  // caller’s selection, so relaying the adjacent post there would add (or remove!) a post the user never chose.
  const removePost = (removedPost, { reselect }) => {
    // We need to determine the next post to select _before_ we propagate the removal.
    const postIndexToReplace = posts.findIndex(post => post.id === removedPost.id)

    // We select the post after the removed one unless it was the last post.
    const postToSelect = posts[postIndexToReplace + (posts.length - 1 === postIndexToReplace ? -1 : 1)]
    postDeleted(removedPost)

    if (!reselect) {
      return
    }

    const nextPost = posts.length > 1 ? postToSelect : null

    // After reporting the removal, now we select.
    // TODO This actually doesn’t work as intended; instead, the collection goes to a new document.
    // We’ll leave it be for now. Hope it isn’t missed too much.
    if (multiselect) {
      handleRequestSelect(nextPost)
    } else {
      handlePostSelect(nextPost)
    }
  }

  // When relaying a change in post, remove the media property so that thumbnails don’t load in the list.
  const handlePostChange = post => {
    const { media, ...changedPost } = post
    const { id, priority: changedPriority, inProgress: changedInProgress } = changedPost

    // Changes to posts outside the loaded page don’t affect the list.
    if (!posts.some(post => post.id === id)) {
      return
    }

    const matchesPriority = priority ? changedPriority : true
    const matchesInProgress = inProgress ? changedInProgress : true

    // If the post no longer matches our active filters (e.g. priority toggled inline or tagging completed
    // in the drawer), we remove it from the local list instead of performing a regular cache update.
    if (matchesPriority && matchesInProgress) {
      postUpdated(changedPost)
    } else {
      removePost(changedPost, { reselect: id === selectedPostId })
    }
  }

  const handlePostDelete = deletedPost => removePost(deletedPost, { reselect: true })

  // Whether the selection below was just cleared by a search/filter change, as opposed to there simply
  // being no selection yet. The auto-(re)select effect checks this so it doesn’t immediately re-select the
  // previously-selected post from the stale `posts` array — the refetch for the new filter hasn’t
  // resolved yet at that point, so the old post (matching the still-stale `requestedPostId`/URL) is often
  // still sitting right there in `posts`, which would otherwise snap the URL right back to it.
  const justClearedSelectionRef = useRef(false)

  // Search filter changes clear out the current list of posts.
  const handleSearchChange = setter => value =>
    setter(currentValue => {
      if (currentValue !== value) {
        justClearedSelectionRef.current = true
        handlePostSelect(null)
        return value
      } else {
        return currentValue
      }
    })

  useEffect(() => {
    let active = true

    const loadFilters = async () => {
      const filters = await getTypeFilters()
      if (!active) {
        return
      }

      setFilters(filters)
    }

    loadFilters()
    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    // Post selection can get complicated due to the range of options. These are the rules (so far):
    // - The selected post and the requested post ID must match
    // - If there is no selected post, a post will get selected if autoselectPostId is true

    if (justClearedSelectionRef.current) {
      // A search/filter change just intentionally cleared the selection — skip this one run so we don’t
      // immediately re-select the old post out of the stale `posts` array (see the comment on the ref).
      // Once `posts` actually refreshes for the new filter, this effect re-runs again on its own.
      justClearedSelectionRef.current = false
      return
    }

    // Implemented this as a function so that we don’t do the search until we actually need to.
    const requestedPost = () => (posts ?? []).find(post => post.id === requestedPostId)

    if (selectedPostId) {
      if (requestedPostId === selectedPostId || multiselect) {
        // Early exit if the state of things matches up—i.e., no changes necessary.
        // Also, multiselection is handled outside of this hook so we don't call handlePostSelect here.
        return
      } else {
        // If things don’t line up, it is the requested post ID that wins.
        handlePostSelect(requestedPost())
      }
    } else {
      // If there is no selected post, we can select one if we have posts loaded. If there is a
      // requested ID we use that; if not, we pick one if autoselectPostId is true.
      if (posts?.length > 0) {
        const postToSelect = requestedPostId ? requestedPost() : autoselectPostId ? posts[0] : null

        if (postToSelect && !multiselect) {
          handlePostSelect(postToSelect)
        }
      }
    }
  }, [autoselectPostId, selectedPostId, requestedPostId, posts, clientId, handlePostSelect, multiselect])

  return {
    binderIsEmpty,
    counts,
    categories,
    categoryFilter,
    setCategoryFilter,
    clientId,
    setClientId: handleSearchChange(setClientId),
    clearSearch,
    clearPriority,
    filters,
    posts,
    postsComplete,
    postsPaging,
    nextPostsPage,
    priority,
    setPriority: handleSearchChange(setPriority),
    selectedPost,
    query,
    setQuery: handleSearchChange(setQuery),
    inProgress,
    setInProgress: handleSearchChange(setInProgress),
    onPostAdd: handlePostAdd,
    onPostChange: handlePostChange,
    onPostCreate: handleCreate,
    onPostDelete: handlePostDelete,
    onPostSelect: handlePostSelect,
    orderBy,
    setOrderBy: handleSearchChange(setOrderBy),
    orderDirection,
    setOrderDirection: handleSearchChange(setOrderDirection)
  }
}

// Removing a post’s priority gives the user a chance to undo; the change is only passed on to the caller (and
// announced) once that timeout finalizes.
const usePriorityToggle = ({ post, onPostChange, readOnly }) => {
  const { openSnackbar } = useContext(SnackbarStackContext)

  const { id, priority } = post ?? {}

  const [error, setError] = useState()
  const handleApiError = useMemo(() => getHookPromiseErrorHandler(setError), [])

  const [localIsPrioritized, setLocalIsPrioritized] = useState(priority)
  const [undoTimeoutResolved, setUndoTimeoutResolved] = useState(true)

  // The removal whose undo window is still open: the server already has it but the caller hasn’t been told. The
  // countdown is the only thing that would tell them, so if it stops early — the component unmounts, or the post is
  // swapped out from under it — the cleanup below passes the removal on instead of dropping it.
  const pendingRemovalRef = useRef(null)

  // The post this hook is currently showing (null once unmounted), so that a save finishing late knows whether
  // there is still a countdown to start.
  const activeIdRef = useRef(id)

  // While a priority change is being saved, further clicks are ignored so that saves can’t finish out of order.
  const savingRef = useRef(false)

  // The cleanup runs after the render that ended the countdown, so it reads the latest callbacks from here.
  const latestCallbacksRef = useRef({ onPostChange, openSnackbar })
  useEffect(() => {
    latestCallbacksRef.current = { onPostChange, openSnackbar }
  })

  // Every path where a removal stands — and only those — comes through here, so this is where it is tracked.
  const notifyRemoval = useCallback(removedPost => {
    trackPriorityDocumentRemoved(removedPost.id)

    const { onPostChange, openSnackbar } = latestCallbacksRef.current
    if (onPostChange) {
      onPostChange(model.post({ ...removedPost, priority: false }))
    }

    openSnackbar({ id: `priority-removed-${removedPost.id}`, message: { title: 'Document removed from Priorities' } })
  }, [])

  useEffect(() => {
    activeIdRef.current = id
    return () => {
      activeIdRef.current = null
      if (pendingRemovalRef.current) {
        notifyRemoval(pendingRemovalRef.current)
        pendingRemovalRef.current = null
      }
    }
  }, [id, notifyRemoval])

  useEffect(() => {
    // Either the caller now knows the post’s priority or we have moved on to another post, so there is
    // nothing left to pass on.
    pendingRemovalRef.current = null
    setLocalIsPrioritized(priority)
    setUndoTimeoutResolved(true)
  }, [id, priority])

  const handleFinalizeNotify = useCallback(() => {
    setUndoTimeoutResolved(true)
    pendingRemovalRef.current = null
    notifyRemoval(post)
  }, [post, notifyRemoval])

  const handleUndoCancel = useCallback(async () => {
    setUndoTimeoutResolved(true)
    setLocalIsPrioritized(true)
    pendingRemovalRef.current = null
    savingRef.current = true
    try {
      const updatedPost = await patchPost({ id, priority: true })
      trackPriorityDocumentAddedByUndo(id)

      if (onPostChange) {
        onPostChange(model.post(updatedPost))
      }
    } catch (error) {
      // The removal stands on the server, so the caller still needs to hear about it.
      setLocalIsPrioritized(false)
      notifyRemoval(post)
      handleApiError(error)
    } finally {
      savingRef.current = false
    }
  }, [id, post, onPostChange, notifyRemoval, handleApiError])

  const handlePriorityClick = useCallback(
    async event => {
      event.stopPropagation()
      event.preventDefault()

      if (readOnly || savingRef.current) {
        return
      }

      const newPriority = !localIsPrioritized
      savingRef.current = true

      // Re-prioritizing during the undo window cancels the pending removal, just like Undo does.
      const cancelledRemoval = newPriority ? pendingRemovalRef.current : null
      if (newPriority) {
        pendingRemovalRef.current = null
        setLocalIsPrioritized(true)
        setUndoTimeoutResolved(true)
      }

      try {
        const updatedPost = await patchPost({ id, priority: newPriority })
        if (newPriority) {
          // Starring again during the undo window cancels the removal, so it counts as undoing it.
          if (cancelledRemoval) {
            trackPriorityDocumentAddedByUndo(id)
          } else {
            trackPriorityDocumentAdded(id)
          }

          if (onPostChange) {
            onPostChange(model.post(updatedPost))
          }
          openSnackbar({ id: `priority-added-${id}`, message: { title: 'Document marked as Priority' } })
        } else if (activeIdRef.current === id) {
          // The undo countdown only starts once the server has the removal, so it can never pass on one that failed.
          pendingRemovalRef.current = post
          setLocalIsPrioritized(false)
          setUndoTimeoutResolved(false)
        } else {
          // We unmounted or moved on to another post while saving, so there is no countdown left to wait for.
          notifyRemoval(post)
        }
      } catch (error) {
        if (newPriority) {
          setLocalIsPrioritized(false)
          if (cancelledRemoval) {
            // The removal we cancelled still stands on the server.
            notifyRemoval(cancelledRemoval)
          }
        }

        handleApiError(error)
      } finally {
        savingRef.current = false
      }
    },
    [id, post, localIsPrioritized, onPostChange, readOnly, openSnackbar, notifyRemoval, handleApiError]
  )

  // Ready-made props for PriorityButton, PriorityUndoPanel, and ErrorSnackbar. The star stays filled through the
  // undo countdown because the removal can still be taken back.
  return {
    buttonProps: {
      priority: localIsPrioritized || !undoTimeoutResolved,
      disabled: readOnly,
      onClick: handlePriorityClick
    },
    undoPanelProps: {
      undoTimeoutResolved,
      setUndoTimeoutResolved,
      onCancel: handleUndoCancel,
      onConfirm: handleFinalizeNotify
    },
    errorProps: { error, setError }
  }
}

export { ALL_CHILDREN, usePostLifeCycle, usePriorityToggle }
